import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import SpeakButton from '../ui/SpeakButton';
import { parseVocabEntry, ARTICLE_STYLES } from '../../lib/vocabGrammar';
import { getEnrichment, VOCAB_SETS } from '../../data/vocab';
import { cn } from '../../lib/utils';

const Section = ({ title, children }) => (
  <section className="space-y-2">
    <h3 className="text-xs font-semibold text-zinc-500">{title}</h3>
    {children}
  </section>
);

const GrammarTile = ({ label, value }) => (
  <div className="min-w-0 rounded-xl border border-zinc-200/80 bg-zinc-50/60 px-3 py-2">
    <dt className="text-[11px] text-zinc-500">{label}</dt>
    <dd className="break-words text-sm font-semibold text-zinc-900">{value}</dd>
  </div>
);

// Shared list for synonyms / antonyms / collocations / examples: { de, sq, note?, register? }
const TermList = ({ items }) => (
  <ul className="divide-y divide-zinc-100">
    {items.map((it, i) => (
      <li key={`${it.de}-${i}`} className="flex items-center gap-2 py-2 first:pt-0 last:pb-0">
        <div className="min-w-0 flex-1">
          <p className="break-words text-sm font-semibold text-zinc-900">{it.de}</p>
          {it.sq && <p className="break-words text-sm text-zinc-500">{it.sq}</p>}
          {(it.note || it.register) && (
            <p className="mt-0.5 text-xs text-zinc-500">{[it.register, it.note].filter(Boolean).join(' · ')}</p>
          )}
        </div>
        <SpeakButton text={it.de} lang="de-DE" className="h-8 w-8 shrink-0" />
      </li>
    ))}
  </ul>
);

// One column of the synonyms | antonyms pair.
const RelationColumn = ({ title, mark, tone, items }) => (
  <section className="flex min-w-0 flex-col rounded-2xl border border-zinc-200/80 bg-white p-4 shadow-sm">
    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-zinc-900">
      <span className={cn('flex h-6 w-6 items-center justify-center rounded-md text-sm font-bold', tone)} aria-hidden="true">
        {mark}
      </span>
      {title}
      <span className="ml-auto text-xs font-normal text-zinc-400">{items.length}</span>
    </h3>
    {items.length ? (
      <TermList items={items} />
    ) : (
      <p className="text-sm text-zinc-400">Nuk ka për këtë kuptim.</p>
    )}
  </section>
);

/**
 * Accessible word-details dialog. Renders only data that exists on the entry or in the
 * verified enrichment file; missing optional fields are simply not shown.
 * Bottom sheet on mobile, centered dialog from `sm` upwards.
 */
const WordDetailsModal = ({ entry, setId, open, onOpenChange, saved, pending, onToggleUnknown }) => {
  if (!entry) return null;
  const g = parseVocabEntry(entry.de);
  const extra = getEnrichment(setId, entry.de);
  const set = VOCAB_SETS[setId];
  const unit = entry.chapterNumber
    ? `Kapitel ${entry.chapterNumber}${entry.module ? ` · Modul ${entry.module}` : ''}`
    : entry.lesson ? `Lektion ${entry.lesson}` : null;
  const synonyms = extra?.synonyms ?? [];
  const antonyms = extra?.antonyms ?? [];
  const hasRelations = synonyms.length > 0 || antonyms.length > 0;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            'fixed z-50 flex flex-col overflow-hidden bg-[#fafaf9] shadow-xl outline-none',
            'inset-x-0 bottom-0 max-h-[90dvh] rounded-t-2xl',
            'sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[85vh] sm:w-[calc(100%-2rem)] sm:max-w-2xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl',
          )}
        >
          <div aria-hidden="true" className="flex h-1 shrink-0">
            <span className="flex-1 bg-zinc-950" />
            <span className="flex-1 bg-[#DD0000]" />
            <span className="flex-1 bg-[#FFCE00]" />
          </div>

          {/* Header */}
          <div className="relative flex items-start gap-3 border-b border-zinc-200/70 bg-white px-4 pb-4 pt-4 sm:px-6">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                {g.article && (
                  <span className={cn('rounded-md border px-2 py-0.5 text-sm font-bold', ARTICLE_STYLES[g.article].className)}>
                    {g.article}
                  </span>
                )}
                <Dialog.Title className="break-words text-2xl font-bold tracking-tight text-zinc-950">{g.word}</Dialog.Title>
                <SpeakButton text={g.article ? `${g.article} ${g.word}` : g.word} lang="de-DE" />
              </div>
              <Dialog.Description className="mt-1 break-words text-base text-zinc-600">
                {entry.sq || 'Pa përkthim'}
              </Dialog.Description>
              <div className="mt-3 flex flex-wrap gap-1.5 text-xs">
                {set && <span className="rounded-lg border border-zinc-200 px-2 py-0.5 font-medium text-zinc-700">{set.level}</span>}
                {unit && <span className="rounded-lg border border-zinc-200 px-2 py-0.5 font-medium text-zinc-700">{unit}</span>}
                {entry.type === 'phrase' && <span className="rounded-lg border border-[#FFCE00]/60 bg-[#FFF8D9] px-2 py-0.5 font-medium text-[#806700]">Shprehje</span>}
                {extra?.register && <span className="rounded-lg border border-zinc-200 px-2 py-0.5 font-medium text-zinc-700">{extra.register}</span>}
              </div>
            </div>
            <Dialog.Close
              className="-mr-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/20"
              aria-label="Mbyll"
            >
              <XMarkIcon className="h-5 w-5" />
            </Dialog.Close>
          </div>

          {/* Scrollable body */}
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6">
            {g.kind === 'noun' && (
              <Section title="Gramatika">
                <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <GrammarTile label="Nyja" value={g.article} />
                  {g.gender && <GrammarTile label="Gjinia" value={g.gender} />}
                  {(g.plural || g.pluralNote) && (
                    <GrammarTile label="Shumësi" value={g.plural || g.pluralNote} />
                  )}
                </dl>
              </Section>
            )}
            {g.kind === 'verb' && (
              <Section title="Format e foljes">
                <dl className="grid grid-cols-2 gap-2">
                  <GrammarTile label="Infinitiv" value={g.infinitive} />
                  {g.praesens && <GrammarTile label="Präsens (er/sie/es)" value={g.praesens} />}
                  {g.praeteritum && <GrammarTile label="Präteritum" value={g.praeteritum} />}
                  <GrammarTile label={`Perfekt (me „${g.auxiliary}“)`} value={g.perfekt} />
                  {extra?.governs && <GrammarTile label="Rasa / parafjala" value={extra.governs} />}
                </dl>
              </Section>
            )}

            {hasRelations && (
              <div className="grid gap-3 sm:grid-cols-2">
                <RelationColumn title="Sinonime" mark="≈" tone="bg-zinc-950 text-[#FFCE00]" items={synonyms} />
                <RelationColumn title="Antonime" mark="≠" tone="bg-[#DD0000] text-white" items={antonyms} />
              </div>
            )}

            {extra?.collocations?.length > 0 && (
              <Section title="Kolokacione dhe shprehje">
                <div className="rounded-2xl border border-zinc-200/80 bg-white p-4 shadow-sm"><TermList items={extra.collocations} /></div>
              </Section>
            )}
            {/* Examples: only stored examples are shown. Future AI generation can plug in here. */}
            {extra?.examples?.length > 0 && (
              <Section title="Shembuj">
                <div className="rounded-2xl border border-zinc-200/80 bg-white p-4 shadow-sm"><TermList items={extra.examples} /></div>
              </Section>
            )}

            {!extra && g.kind === 'other' && (
              <p className="text-sm text-zinc-500">Nuk ka ende informacione shtesë për këtë fjalë.</p>
            )}
          </div>

          {/* Sticky action */}
          <div className="border-t border-zinc-200/70 bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6">
            <button
              type="button"
              onClick={onToggleUnknown}
              disabled={pending}
              aria-pressed={saved}
              className={cn(
                'inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-bold transition-colors disabled:opacity-60 cursor-pointer',
                saved
                  ? 'border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                  : 'bg-zinc-950 text-white hover:bg-emerald-600',
              )}
            >
              {saved ? (
                <>
                  <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
                  E shënuar si e ditur (Kliko për ta hequr)
                </>
              ) : (
                <>
                  <CheckIcon className="h-5 w-5" />
                  E di këtë fjalë (Shëno si të ditur)
                </>
              )}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default WordDetailsModal;
